import { buildSunoInjectionScript, resolveStyleTags } from './llmShared';
import type { ParsedSunoOutput } from '@/types';

/**
 * Checks that run AFTER a song is assembled and can't be skipped by the model.
 * They only report, they never rewrite the song. (Studio Team v2.6.20 handoff, items 2 and 3.)
 *
 * Both guard bugs that were fixed once and must not come back silently:
 *  - the Suno console script was built from a snapshot taken before Riff DNA was appended
 *    (fixed by finalizeGeneratedResult rebuilding it last, commit a243135 + cd043d3)
 *  - InstrumentForge output looked complete in the preview but never reached the prompt
 *    builder, so {LAYER-2}..{LAYER-5} were missing from songs (fixed by auto-apply, a243135)
 */

type Injected = { title: string; style: string; lyrics: string };

/** Pull the three values the console script will type into Suno back out of the script text. */
export function parseInjection(script: string): Injected | null {
  const str = String.raw`("(?:[^"\\]|\\.)*")`; // a JSON string literal
  const lyrics = script.match(new RegExp(String.raw`^injectLyrics\(${str}\);$`, 'm'));
  const style = script.match(new RegExp(String.raw`setNativeValue\(styleInput, ${str}\)`));
  const title = script.match(new RegExp(String.raw`setNativeValue\(songTitleInput, ${str}\)`));
  if (!lyrics || !style || !title) return null;
  try {
    return { lyrics: JSON.parse(lyrics[1]), style: JSON.parse(style[1]), title: JSON.parse(title[1]) };
  } catch {
    return null;
  }
}

const firstDiff = (a: string, b: string): number => {
  const n = Math.min(a.length, b.length);
  for (let i = 0; i < n; i++) if (a[i] !== b[i]) return i;
  return a.length === b.length ? -1 : n;
};

/**
 * Documented vs injected: the script on the output must be exactly what the output's own final
 * fields would produce. Returns a problem message, or null when they match.
 */
export function checkInjectionMatches(output: ParsedSunoOutput): string | null {
  const expectedScript = buildSunoInjectionScript(
    output.title,
    resolveStyleTags(output),
    output.lyricsWithTags || '',
    output.weirdness ?? 65,
    output.styleStrength ?? 85,
  );
  if (output.javascriptCode === expectedScript) return null;

  const injected = parseInjection(output.javascriptCode || '');
  const expected = parseInjection(expectedScript);
  if (!injected || !expected) {
    return `Suno console script does not match the song (could not read the script, ${(output.javascriptCode || '').length} chars).`;
  }
  const names = { title: 'title', style: 'Suno Tags', lyrics: 'lyrics' } as const;
  const bad = (Object.keys(names) as (keyof Injected)[])
    .filter((key) => injected[key] !== expected[key])
    .map((key) => `${names[key]} (script ${injected[key].length} chars / song ${expected[key].length}, first difference at ${firstDiff(injected[key], expected[key])})`);
  // The sliders can differ without any text field differing.
  return bad.length
    ? `Suno console script does not match the song: ${bad.join('; ')}. Copy the song fields, not the script, until this is fixed.`
    : 'Suno console script does not match the song (the slider values differ).';
}

/** The layer numbers named in a {LAYER-n ...} block of text, in order, no repeats. */
const layersIn = (text: string): number[] => {
  const found: number[] = [];
  for (const m of text.matchAll(/\{LAYER-(\d+)/g)) {
    const n = Number(m[1]);
    if (!found.includes(n)) found.push(n);
  }
  return found;
};

/**
 * If a forge module produced output, that output must be in the assembled song. "Present" means the
 * {LAYER-n} tags are in the lyrics, not that a preview rendered. Returns a problem message, or null.
 * `forgePhysics` is what the shared context hands the prompt builder (empty when no forge is active).
 */
export function checkForgeDelivered(lyricsWithTags: string, forgePhysics: string): string | null {
  const wanted = layersIn(forgePhysics).filter((n) => n >= 2 && n <= 5);
  if (!wanted.length) return null;
  const present = layersIn(lyricsWithTags);
  const missing = wanted.filter((n) => !present.includes(n));
  if (!missing.length) return null;
  return `InstrumentForge is active but ${missing.map((n) => `{LAYER-${n}}`).join(', ')} ${missing.length === 1 ? 'is' : 'are'} missing from the song. Its output did not reach the assembled prompt.`;
}

/** Every problem worth showing for a freshly assembled song, as plain sentences. */
export function deliveryProblems(output: ParsedSunoOutput, forgePhysics?: string): string[] {
  return [
    checkInjectionMatches(output),
    forgePhysics === undefined ? null : checkForgeDelivered(output.lyricsWithTags || '', forgePhysics),
  ].filter((p): p is string => p !== null);
}
